
import React from 'react';
import { Alert, Modal, Spinner } from 'react-bootstrap';
import { Show } from 'Layout';
import { PageStatus } from 'enums';
import Select from 'react-select';
import {AuthAPI, MasterDataAPI} from "../../API";
import { Container } from '@material-ui/core';
import {CountriesAPI} from "../../API/CountriesAPI";
import {reduxForm} from "redux-form";
import {withRouter} from "react-router";
import moment from "moment/moment";
import { formFieldDict } from 'Languages/SettingTranslations';
import { registerDict } from 'Languages/RegistrationTranslations';
import { withTranslation } from "react-i18next";
import { PORTAL_CODE } from '../../config/portalBranding';
import { JP_POSTCODE_MAP } from '../../config/jpPostcodeMap';

// 47 Japanese prefectures (standard romanized names, alphabetical).
// Used only for the Japan-specific Update Profile layout. The states API
// is intentionally not trusted here — the JP states data was inherited
// from a UK import and is unreliable; a static array keeps it deterministic.
const JP_PREFECTURES = [
    'Aichi', 'Akita', 'Aomori', 'Chiba', 'Ehime', 'Fukui', 'Fukuoka',
    'Fukushima', 'Gifu', 'Gunma', 'Hiroshima', 'Hokkaido', 'Hyogo',
    'Ibaraki', 'Ishikawa', 'Iwate', 'Kagawa', 'Kagoshima', 'Kanagawa',
    'Kochi', 'Kumamoto', 'Kyoto', 'Mie', 'Miyagi', 'Miyazaki', 'Nagano',
    'Nagasaki', 'Nara', 'Niigata', 'Oita', 'Okayama', 'Okinawa', 'Osaka',
    'Saga', 'Saitama', 'Shiga', 'Shimane', 'Shizuoka', 'Tochigi',
    'Tokushima', 'Tokyo', 'Tottori', 'Toyama', 'Wakayama', 'Yamagata',
    'Yamaguchi', 'Yamanashi',
].map((p) => ({ label: p, value: p }));

const IS_JAPAN_PORTAL = PORTAL_CODE === 'JP';

class BasicProfile extends React.Component<any, any> {
    constructor(props) {
        super(props);
        this.state = {
            status: PageStatus.None,
            error: null,
            firstName: '',
            lastName: '' ,
            // Japan layout uses a single "Full Name" input that is split into
            // firstName/lastName on submit; this backs that input.
            fullName: '',
            mobile: this.props.phoneNumber || '',
            gender: 'Male',
            dateOfBirth: '',
            selectedCountryOption: null,
            selectedStateOption: null,
            selectedCityOption: null,
            countryOptions: [],
            country: '',
            addressLine1: '',
            addressLine2: '',
            state: '',
            city: '',
            pinCode: '',
            referralSource: '',
            countries: [],
            states: [],
            cities: [],
            profile: null,
            email: '',
            oldValues: {},
            oldEmailValue: '',
            errors: {},
        };
    }

    async componentDidMount() {
        this.fetchUserProfile()
    }


    fetchUserProfile(): Promise<void> {
        return Promise.resolve()
            .then(() => this.setState({ status: PageStatus.Loading }))
            .then(() => AuthAPI.profile(this.props.userId))
            .then((profile) => {
                this.initializeValues(profile.profile)
                this.setState({ email: (profile.dataValues.email ? profile.dataValues.email : ''), oldEmailValue: (profile.dataValues.email ? profile.dataValues.email : ''), })
                this.fetchCountryList()
                this.fetchAllStates(profile.profile.state, profile.profile.city)
                this.setState({ profile, status: PageStatus.Loaded });
            })
            .catch((error) => {
                this.setState({ error: error.message, status: PageStatus.Error });
            });
    }


    initializeValues(data) {
        return this.setState({
            firstName: data.firstName,
            lastName: data.lastName,
            // Japan layout: pre-fill the single Full Name input from the
            // loaded firstName/lastName so the field is populated on edit.
            fullName: IS_JAPAN_PORTAL
                ? `${data.firstName || ''} ${data.lastName || ''}`.trim()
                : '',
            mobile: data.mobile,
            gender: data.gender,
            dateOfBirth: moment(data.dateOfBirth).format("YYYY-MM-DD"),
            country: data.country,
            addressLine1: data.addressLine1,
            addressLine2: data.addressLine2 || '',
            state: data.state,
            city: data.city,
            pinCode: data.pinCode,
            referralSource: data.referralSource,
            acceptTerms: true,
            oldValues: data,
        });
    }


    formValues() {
        if (IS_JAPAN_PORTAL) {
            // Japan layout: firstName/lastName are derived from the Full Name
            // split (not pulled from stale oldValues). `country` is forced to
            // 'JP'; `mobile` is '' (phone eliminated from the flow); there is
            // no addressLine2 input, so it defaults to 'NA'. The '' mobile and
            // ' ' lastName fallbacks replicate BasicProfile.tsx — they exist
            // specifically to pass the backend validators.
            return {
                firstName: this.state.firstName,
                lastName: this.state.lastName,
                mobile: '',
                gender: this.state.gender,
                dateOfBirth: this.state.dateOfBirth,
                country: 'JP',
                addressLine1: this.state.addressLine1,
                addressLine2: 'NA',
                state: this.state.state,
                city: this.state.city,
                pinCode: this.state.pinCode,
                referralSource: this.state.referralSource,
                acceptTerms: true,
                email: this.state.oldEmailValue ? this.state.oldEmailValue : this.state.email,
            };
        }
        return {
            firstName: this.state.oldValues.firstName ? this.state.oldValues.firstName : this.state.firstName,
            lastName: this.state.oldValues.lastName ? this.state.oldValues.lastName : this.state.lastName,
            mobile: this.state.oldValues.mobile ? this.state.oldValues.mobile : this.state.mobile,
            gender: this.state.gender,
            dateOfBirth: this.state.oldValues.dateOfBirth ? this.state.oldValues.dateOfBirth : this.state.dateOfBirth,
            country: this.state.country,
            addressLine1: this.state.addressLine1,
            addressLine2: this.state.addressLine2 || 'NA',
            state: this.state.state,
            city: this.state.city,
            pinCode: this.state.pinCode,
            referralSource: this.state.referralSource,
            acceptTerms: true,
            email: this.state.oldEmailValue ? this.state.oldEmailValue : this.state.email,
        };
    }


    // Japan layout uses manual validation (the JP <form> is noValidate).
    // Mirrors BasicProfile.tsx validateForm() lines 115-129.
    checkValidDateOfBirth = () => {
        const enteredDate = this.state.dateOfBirth;
        const currentDate = new Date();
        const minAgeDate = new Date(currentDate.getFullYear() - 100, currentDate.getMonth(), currentDate.getDate());
        const maxAgeDate = new Date(currentDate.getFullYear() - 16, currentDate.getMonth(), currentDate.getDate());
        const selectedDate = new Date(enteredDate);
        const enteredTimestamp = selectedDate.getTime();
        const minAgeTimestamp = minAgeDate.getTime();
        const maxAgeTimestamp = maxAgeDate.getTime();
        if (!isNaN(selectedDate) && enteredTimestamp >= minAgeTimestamp && enteredTimestamp <= maxAgeTimestamp) {
            return true;
        }
        return false;
    }

    validateForm = () => {
        let errors = {};
        // Japan layout: Full Name, Date Of Birth, Gender, Address Line,
        // City, Prefecture, Postcode, Referral Source. mobile/country are
        // silent defaults and are not validated here.
        if (!this.state.fullName.trim()) { errors.fullName = "氏名は必須です"; }
        if (!this.state.gender) { errors.gender = "性別は必須です"; }
        if (!this.state.dateOfBirth) { errors.dateOfBirth = "生年月日は必須です"; }
        if (!this.state.addressLine1.trim()) { errors.addressLine1 = "住所は必須です"; }
        if (!this.state.city.trim()) { errors.city = "市区町村は必須です"; }
        if (!this.state.state) { errors.state = "都道府県は必須です"; }
        if (!/^\d{3}-\d{4}$/.test(this.state.pinCode)) {
            errors.pinCode = "郵便番号はNNN-NNNN形式で入力してください（例：100-0001）";
        } else if (!JP_POSTCODE_MAP[this.state.pinCode.replace(/[\s-]/g, '').slice(0, 3)]) {
            errors.pinCode = "有効な日本の郵便番号を入力してください";
        }
        if (!this.state.referralSource) { errors.referralSource = "紹介元は必須です"; }
        if (!this.checkValidDateOfBirth()) { errors.dateOfBirth = "パネリストとして登録するには、16歳以上である必要があります。"; }
        this.setState({ errors });
        return Object.keys(errors).length === 0;
    };

    onSubmit() {
        if (this.props.userId) {
            if (IS_JAPAN_PORTAL && !this.validateForm()) { return; }
            return this.createProfile();
        }
    }

    createProfile() {
        if(this.props.userId) {
            const valuesIn = this.formValues()
            return Promise.resolve()
                .then(() => this.setState({status: PageStatus.Submitting}))
                .then(() => AuthAPI.createBasicProfile(valuesIn, this.props.userId, this.props.language || 'en'))
                .then((profile) => {
                    this.fetchUserProfile()
                    alert(this.props.t('settings.profileUpdatedAlert'))
                    this.setState({status: PageStatus.Submitted}, () => {
                        this.props.onSubmit()
                    });
                })
                .catch((error) => {
                    this.setState({status: PageStatus.Error, error: error.message});
                });
        }
    }



    fetchCountryList(): Promise<void> {
        return Promise.resolve()
            .then(() => this.setState({ status: PageStatus.Loading }))
            .then(() => MasterDataAPI.countryList('10'))
            .then((countries) => {
                const options = countries.map(country => ({
                    label: this.props.language === 'hi' ? country.hindi : country.name,
                    value: country.title
                }));
                this.setState({ countries: options, status: PageStatus.Loaded });
                if(this.state.country) {
                    const option = options.find(item => item.value === this.state.country);
                    this.setState({ selectedCountryOption: option });
                }
            })
            .catch((error) => {
                this.setState({ error: error.message, status: PageStatus.Error });
            });
    }


    reset() {
        return this.setState({
            firstName: '',
            lastName: '' ,
        });
    }

    fetchAllStates(savedState = null, savedCity = null): Promise<void> {
        return CountriesAPI.getAllStates(1000)
            .then((statesList) => {
                const options = statesList.map(s => ({
                    label: this.props.language === 'hi' ? s.hindi : s.name,
                    value: s.name,
                    id: s.id,
                }));
                options.sort((a, b) => a.label < b.label ? -1 : a.label > b.label ? 1 : 0);
                this.setState({ states: options });
                if (savedState) {
                    const match = options.find(o => o.value === savedState);
                    if (match) {
                        this.setState({ selectedStateOption: match });
                        this.fetchCitiesByStateId(match.id, savedCity);
                    }
                }
            })
            .catch((error) => {
                this.setState({ error: error.message });
            });
    }

    fetchCitiesByStateId(stateId, savedCity = null): Promise<void> {
        return CountriesAPI.getUniqueDistrictByStateIds({ stateIds: [stateId] })
            .then((districts) => {
                const options = districts
                    .filter(d => d.segment)
                    .map(d => ({
                        label: d.segment,
                        value: d.segment,
                    }));
                options.sort((a, b) => a.label < b.label ? -1 : a.label > b.label ? 1 : 0);
                this.setState({ cities: options });
                if (savedCity) {
                    const match = options.find(o => o.value === savedCity);
                    if (match) this.setState({ selectedCityOption: match });
                }
            })
            .catch((error) => {
                this.setState({ error: error.message });
            });
    }

    handleCountryChange = async (selectedCountryOption) => {
        this.setState({
            country: selectedCountryOption.value,
            selectedCountryOption,
            states: [], cities: [],
            selectedStateOption: null, selectedCityOption: null,
            state: '', city: '',
        });
        this.fetchAllStates();
    };

    handleStateChange = async (selectedStateOption) => {
        this.setState({
            state: selectedStateOption.value,
            selectedStateOption,
            cities: [], selectedCityOption: null, city: '',
        });
        if (selectedStateOption.id) {
            this.fetchCitiesByStateId(selectedStateOption.id);
        }
    };

    handleCityChange = async (selectedCityOption) => {
        this.setState({ city: selectedCityOption.value, selectedCityOption });
    };

    handleDateOfBirthChange(e) {
        const enteredDate = e.target.value;

        const currentDate = new Date();
        const minAgeDate = new Date(currentDate.getFullYear() - 100, currentDate.getMonth(), currentDate.getDate());
        const maxAgeDate = new Date(currentDate.getFullYear() - 16, currentDate.getMonth(), currentDate.getDate());

        // Parse the entered date in the "YYYY-MM-DD" format
        const selectedDate:any = new Date(enteredDate);

        const enteredTimestamp = selectedDate.getTime();
        const minAgeTimestamp = minAgeDate.getTime();
        const maxAgeTimestamp = maxAgeDate.getTime();
        if (!isNaN(selectedDate) && enteredTimestamp >= minAgeTimestamp && enteredTimestamp <= maxAgeTimestamp) {

            this.setState({ dateOfBirth: enteredDate });

            e.target.setCustomValidity("");
        } else {
            e.target.value="";
            console.error("Invalid age range. Please enter a date of birth between 16 and 100 years.");
            e.target.setCustomValidity(`${this.props.t('settings.dobValidationMessage')}`);
        }

        e.target.reportValidity();
    }


    handleZipCodeChange = (event) => {
        const { value } = event.target;
        this.setState({ pinCode: value });
        if (value.length === 6) {
            CountriesAPI.getAllCitiesAndStateBasedOnZipCode(1000, value)
                .then((result: any) => {
                    if (result.state && result.state.length > 0) {
                        const stateName = result.state[0].title || result.state[0].name;
                        const stateMatch = this.state.states.find((s: any) => s.value === stateName);
                        if (stateMatch) {
                            this.setState({ state: stateMatch.value, selectedStateOption: stateMatch });
                            this.fetchCitiesByStateId(stateMatch.id);
                        }
                    }
                })
                .catch(() => {});
        } else {
            this.setState({ state: '', selectedStateOption: null, city: '', selectedCityOption: null });
        }
    };

    // --- Japan-specific handlers (ported from BasicProfile.tsx) ---------

    // Full Name -> split on first space into firstName + lastName.
    // No space (or empty last part): lastName falls back to a single space.
    // The backend updateValidator requires lastName length >= 1; a single
    // space satisfies that without distorting the name.
    handleFullNameChange = (e) => {
        const value = e.target.value;
        const trimmed = value.trim();
        const idx = trimmed.indexOf(' ');
        const firstName = idx === -1 ? trimmed : trimmed.slice(0, idx);
        const lastNameRaw = idx === -1 ? '' : trimmed.slice(idx + 1).trim();
        const lastName = lastNameRaw === '' ? ' ' : lastNameRaw;
        this.setState((prev) => ({
            fullName: value,
            firstName,
            lastName,
            errors: { ...prev.errors, fullName: "" },
        }));
    };

    handleJpPrefectureChange = (selectedStateOption) => {
        // Japan prefecture maps to the `state` column. Free-text city is used
        // on the JP layout, so no dependent city lookup is triggered here.
        this.setState((prev) => ({
            state: selectedStateOption ? selectedStateOption.value : '',
            selectedStateOption,
            errors: { ...prev.errors, state: "" },
        }));
    };

    // Guard against repeating the same lookup.
    lastLookupPostcode = '';

    // Postcode -> pinCode column. Accepts digits and a single hyphen,
    // formats toward NNN-NNNN; full validation happens in validateForm.
    // When a complete NNN-NNNN value is entered, triggers the JP postcode
    // autocomplete to fill Prefecture + City.
    handleJpPostcodeChange = (e) => {
        let value = e.target.value.replace(/[^\d-]/g, '');
        const digits = value.replace(/-/g, '').slice(0, 7);
        value = digits.length > 3 ? `${digits.slice(0, 3)}-${digits.slice(3)}` : digits;
        this.setState((prev) => ({ pinCode: value, errors: { ...prev.errors, pinCode: "" } }));
        if (/^\d{3}-\d{4}$/.test(value)) {
            this.lookupJpPostcode(digits);
        } else {
            // Reset the guard so re-entering a complete postcode looks up again.
            this.lastLookupPostcode = '';
        }
    };

    // Japan postcode autocomplete — synchronous in-memory lookup against
    // JP_POSTCODE_MAP. Looks up the first 3 digits and auto-fills
    // Prefecture + City. Silent on miss: fields are left untouched for
    // manual entry, and submission is never blocked.
    lookupJpPostcode = (postcode) => {
        const normalized = String(postcode || '').replace(/[\s-]/g, '');
        if (!normalized || normalized === this.lastLookupPostcode) {
            return;
        }
        this.lastLookupPostcode = normalized;

        const hit = JP_POSTCODE_MAP[normalized.slice(0, 3)];
        if (hit) {
            this.setState((prev) => ({
                city: hit.city,
                state: hit.prefecture,
                selectedStateOption: { label: hit.prefecture, value: hit.prefecture },
                errors: { ...prev.errors, city: "", state: "" },
            }));
        }
        // Miss: do nothing — silent, manual entry remains usable.
    };



    render() {
        const {selectedCountryOption, selectedStateOption, selectedCityOption}=this.state
        const lang = this.props.language ?? 'en';
        return (
            <Modal
                centered
                size="xl"
                backdrop="static"
                onHide={this.props.onClose}
                show={this.props.show}
                style={{ zIndex: 1201 }}
            >
                <Modal.Header closeButton>
                    <Modal.Title>
                        {formFieldDict[lang]["Update Profile"] || "Update Profile"}
                    </Modal.Title>
                </Modal.Header>

                <Modal.Body style={{ maxHeight: '78vh', overflow: 'auto' }}>
                    <Container maxWidth="lg">
                        <Show when={this.state.status === PageStatus.Loading}>
                            <div className="d-flex justify-content-center w-100 p-5">
                                <Spinner animation="border" variant="primary" />
                            </div>
                        </Show>

                        <Alert variant="danger" show={this.state.status === PageStatus.Error}>
                            {this.state.error}
                        </Alert>
                        <form
                            noValidate={IS_JAPAN_PORTAL}
                            onSubmit={this.props.handleSubmit((event) => this.onSubmit())}
                            className="mt-4"
                        >

                            {IS_JAPAN_PORTAL ? (
                            <>
                                {/* ===== Japan-specific Update Profile layout ===== */}
                                <div className="row">
                                    <div className="col">
                                        <label htmlFor="fullName">{formFieldDict[lang]["Full Name"] || "Full Name"}*</label>
                                        <input
                                            className="form-control"
                                            id="fullName"
                                            name="fullName"
                                            onChange={this.handleFullNameChange}
                                            value={this.state.fullName}
                                            placeholder={formFieldDict[lang]["Enter here"] || "Enter here"}
                                        />
                                        {this.state.errors.fullName && (
                                            <p className="text-danger" style={{ fontSize: "12px" }}>{this.state.errors.fullName}</p>
                                        )}
                                    </div>
                                    <div className="col">
                                        <label htmlFor="emailJp">{formFieldDict[lang]["Email"] || "Email"}</label>
                                        <input
                                            className="form-control"
                                            id="emailJp"
                                            name="email"
                                            type="email"
                                            value={this.state.email}
                                            readOnly
                                            disabled
                                        />
                                    </div>
                                    <div className="col">
                                        <label htmlFor="dateOfBirthJp">{formFieldDict[lang]["Date Of Birth"] || "Date Of Birth"}*</label>
                                        <input
                                            className="form-control"
                                            id="dateOfBirthJp"
                                            type="date"
                                            name="dateOfBirth"
                                            onChange={(e) => this.setState((prev) => ({ dateOfBirth: e.target.value, errors: { ...prev.errors, dateOfBirth: "" } }))}
                                            value={this.state.dateOfBirth}
                                        />
                                        {this.state.errors.dateOfBirth && (
                                            <p className="text-danger" style={{ fontSize: "12px" }}>{this.state.errors.dateOfBirth}</p>
                                        )}
                                    </div>
                                </div>

                                <div className="row">
                                    <div className="col">
                                        <label htmlFor="genderJp">{formFieldDict[lang]["Gender"] || "Gender"}*</label>
                                        <select
                                            name="gender"
                                            id="genderJp"
                                            value={this.state.gender}
                                            onChange={(e) => this.setState({ gender: e.target.value })}
                                            style={{
                                                width: '100%',
                                                display: 'block',
                                                height: '40px',
                                                lineHeight: '1.5',
                                                color: '#495057',
                                                backgroundColor: '#fff',
                                                backgroundClip: 'padding-box',
                                                border: '1px solid #ced4da',
                                                borderRadius: '5px',
                                                transition: 'border-color 0.15s ease-in-out, box-shadow 0.15s ease-in-out',
                                            }}
                                        >
                                            <option value="" disabled>{formFieldDict[lang]["Choose"] || "--Choose--"}</option>
                                            <option value="Male">{formFieldDict[lang]["Male"] || "Male"}</option>
                                            <option value="Female">{formFieldDict[lang]["Female"] || "Female"}</option>
                                            <option value="Other">{formFieldDict[lang]["Other"] || "Other"}</option>
                                        </select>
                                    </div>
                                    <div className="col">
                                        <label htmlFor="addressLineJp">{formFieldDict[lang]["Address Line"] || "Address Line"}*</label>
                                        <input
                                            className="form-control"
                                            id="addressLineJp"
                                            name="addressLine1"
                                            value={this.state.addressLine1}
                                            onChange={(e) => this.setState((prev) => ({ addressLine1: e.target.value, errors: { ...prev.errors, addressLine1: "" } }))}
                                            placeholder={formFieldDict[lang]["Enter here"] || "Enter here"}
                                        />
                                        {this.state.errors.addressLine1 && (
                                            <p className="text-danger" style={{ fontSize: "12px" }}>{this.state.errors.addressLine1}</p>
                                        )}
                                    </div>
                                    <div className="col">
                                        <label htmlFor="cityJp">{formFieldDict[lang]["City"] || "City"}*</label>
                                        <input
                                            className="form-control"
                                            id="cityJp"
                                            name="city"
                                            value={this.state.city}
                                            onChange={(e) => this.setState((prev) => ({ city: e.target.value, errors: { ...prev.errors, city: "" } }))}
                                            placeholder={formFieldDict[lang]["Enter here"] || "Enter here"}
                                        />
                                        {this.state.errors.city && (
                                            <p className="text-danger" style={{ fontSize: "12px" }}>{this.state.errors.city}</p>
                                        )}
                                    </div>
                                </div>

                                <div className="row">
                                    <div className="col">
                                        <label htmlFor="prefecture">{formFieldDict[lang]["Prefecture"] || "Prefecture"}*</label>
                                        <Select
                                            name="prefecture"
                                            id="prefecture"
                                            onChange={this.handleJpPrefectureChange}
                                            value={selectedStateOption}
                                            placeholder={formFieldDict[lang]["Prefecture"] || "Prefecture"}
                                            options={JP_PREFECTURES}
                                        />
                                        {this.state.errors.state && (
                                            <p className="text-danger" style={{ fontSize: "12px" }}>{this.state.errors.state}</p>
                                        )}
                                    </div>
                                    <div className="col">
                                        <label htmlFor="postcodeJp">{formFieldDict[lang]["Postcode"] || "Postcode"}*</label>
                                        <input
                                            className="form-control"
                                            id="postcodeJp"
                                            name="pinCode"
                                            value={this.state.pinCode}
                                            onChange={this.handleJpPostcodeChange}
                                            placeholder="NNN-NNNN（例：100-0001）"
                                        />
                                        {this.state.errors.pinCode && (
                                            <p className="text-danger" style={{ fontSize: "12px" }}>{this.state.errors.pinCode}</p>
                                        )}
                                    </div>
                                    <div className="col">
                                        <label htmlFor="referralSourceJp">{formFieldDict[lang]["Referral Source"] || "Referral Source"}*</label>
                                        <select
                                            style={{
                                                width: '100%',
                                                display: 'block',
                                                height: '40px',
                                                lineHeight: '1.5',
                                                color: '#495057',
                                                backgroundColor: '#fff',
                                                backgroundClip: 'padding-box',
                                                border: `1px solid ${this.state.errors.referralSource ? 'red' : '#ced4da'}`,
                                                borderRadius: '5px',
                                                transition: 'border-color 0.15s ease-in-out, box-shadow 0.15s ease-in-out',
                                            }}
                                            name="referralSource"
                                            id="referralSourceJp"
                                            value={this.state.referralSource}
                                            onChange={(e) => this.setState((prev) => ({ referralSource: e.target.value, errors: { ...prev.errors, referralSource: "" } }))}
                                        >
                                            <option value="">{formFieldDict[lang]["Select Referral"] || "Select Referral"}</option>
                                            <option value="Ad flier with newspaper">{formFieldDict[lang]["Ad flier with newspaper"] || "Ad flier with newspaper"}</option>
                                            <option value="Ad flier at market/outdoors">{formFieldDict[lang]["Ad flier at market/outdoors"] || "Ad flier at market/outdoors"}</option>
                                            <option value="Saw an ad on a website">{formFieldDict[lang]["Saw an ad on a website"] || "Saw an ad on a website"}</option>
                                            <option value="Read about it on a blog/forum">{formFieldDict[lang]["Read about it on a blog/forum"] || "Read about it on a blog/forum"}</option>
                                            <option value="Ad at Café Coffee Day">{formFieldDict[lang]["Ad at Café Coffee Day"] || "Ad at Café Coffee Day"}</option>
                                            <option value="Got an email from a friend/colleague">{formFieldDict[lang]["Got an email from a friend/colleague"] || "Got an email from a friend/colleague"}</option>
                                            <option value="Email from a web portal/service/jobsite">{formFieldDict[lang]["Email from a web portal/service/jobsite"] || "Email from a web portal/service/jobsite"}</option>
                                            <option value="Searched on google.com">{formFieldDict[lang]["Searched on google.com"] || "Searched on google.com"}</option>
                                            <option value="Other">{formFieldDict[lang]["Other"] || "Other"}</option>
                                            <option value="Searched on another search engine">{formFieldDict[lang]["Searched on another search engine"] || "Searched on another search engine"}</option>
                                            <option value="Ad at a shop/restaurant">{formFieldDict[lang]["Ad at a shop/restaurant"] || "Ad at a shop/restaurant"}</option>
                                            <option value="Word of mouth from friend/colleague">{formFieldDict[lang]["Word of mouth from friend/colleague"] || "Word of mouth from friend/colleague"}</option>
                                        </select>
                                        {this.state.errors.referralSource && (
                                            <p className="text-danger" style={{ fontSize: "12px" }}>{this.state.errors.referralSource}</p>
                                        )}
                                    </div>
                                </div>
                            </>
                            ) : (
                            <>
                            <div className="row">
                                <div className="col">
                                    <label htmlFor="firstName">{formFieldDict[lang]["First name"] || "First name"}*</label>
                                    <input
                                        className="form-control"
                                        name="firstName"
                                        onChange={(e) => this.setState({ firstName: e.target.value })}
                                        value={this.state.firstName}
                                        placeholder={formFieldDict[lang]["Enter here"] || "Enter here"}
                                        disabled= {this.state.oldValues.firstName != "" && this.state.oldValues.firstName != undefined }
                                        required
                                    />
                                </div>
                                <div className="col">
                                    <label htmlFor="lastName">{formFieldDict[lang]["Last Name"] || "Last Name"}</label>
                                    <input
                                        className="form-control"
                                        id="lastName"
                                        name="lastName"
                                        onChange={(e) => this.setState({ lastName: e.target.value })}
                                        value={this.state.lastName}
                                        placeholder={formFieldDict[lang]["Enter.."] || "Enter.."}
                                        disabled={this.state.oldValues.lastName != "" && this.state.oldValues.lastName != undefined }
                                    />
                                </div>
                                <div className="col">
                                    <label htmlFor="email">{formFieldDict[lang]["Email"] || "Email"}</label>
                                    <input
                                        className="form-control"
                                        id="email"
                                        name="email"
                                        type="email"
                                        onChange={(e) => this.setState({ email: e.target.value })}
                                        value={this.state.email}
                                        placeholder={formFieldDict[lang]["Email"] || "Email"}
                                        maxLength={200}
                                        onInvalid={(event)=>{
                                        const e = event.target ;
                                          let msg = registerDict[lang]['email_required'];
                                          e.setCustomValidity(msg)
                                        }}
                                        onInput={(event)=>{
                                            const e = event.target ;
                                            e.setCustomValidity("")}}
                                        required
                                        disabled={this.state.oldEmailValue != "" && this.state.oldEmailValue != undefined}
                                    />
                                </div>
                            </div>


                            <div className="row">
                                <div className="col">
                                    <label htmlFor="mobile">{formFieldDict[lang]["Mobile Number"] || "Mobile Number"}*</label>
                                    <input
                                        className="form-control"
                                        id="mobile"
                                        name="mobile"
                                        disabled={this.state.oldValues.mobile != "" && this.state.oldValues.mobile != undefined }
                                        onChange={(e) => this.setState({ mobile: e.target.value })}
                                        value={this.state.mobile}
                                        placeholder={formFieldDict[lang]["Enter here.."] || "Enter here.."}
                                        required
                                        title={registerDict[lang]['phoneTitle']}
                                        pattern="[6-9]\d{9}"
                                        onInvalid={(e)=>{
                                          const evt = e.target ;
                                          let msg = registerDict[lang]['validationMessage'] + ': '+ registerDict[lang]['phoneTitle']
                                          evt.setCustomValidity(msg)
                                        }}
                                        onInput={(e)=>{
                                            const evt = e.target ;
                                            evt.setCustomValidity("")}
                                        }
                                    />
                                </div>
                            </div>

                            <div className="row">
                                <div className="col">
                                    <label htmlFor='gender'>{formFieldDict[lang]["Gender"] || "Gender"}*</label>
                                    <select
                                        name='gender'
                                        id='gender'
                                        value={this.state.gender}
                                        required
                                        disabled
                                        onChange={(e) => this.setState({ gender: e.target.value })}
                                        style={{
                                            width: '100%',
                                            display: 'block',
                                            height: '40px',
                                            lineHeight: '1.5',
                                            color: '#495057',
                                            backgroundColor: '#fff',
                                            backgroundClip: 'padding-box',
                                            border: '1px solid #ced4da',
                                            borderRadius: '5px',
                                            transition:
                                                'border-color 0.15s ease-in-out, box-shadow 0.15s ease-in-out',
                                        }}
                                    >
                                        <option value='' disabled>{formFieldDict[lang]["Choose"] || "--Choose--"}</option>
                                        <option value='Male'>{formFieldDict[lang]["Male"] || "Male"}</option>
                                        <option value='Female'>{formFieldDict[lang]["Female"] || "Female"}</option>
                                        <option value='Other'>{formFieldDict[lang]["Other"] || "Other"}</option>
                                    </select>
                                </div>
                                <div className="col">
                                    <label htmlFor="description">{formFieldDict[lang]["Date Of Birth"] || "Date Of Birth"}*</label>
                                    <input
                                        className="form-control"
                                        id="description"
                                        type="date"
                                        name="description"
                                        onChange={(e) => this.handleDateOfBirthChange(e)}
                                        onKeyDown={(e) => e.preventDefault()}
                                        value={this.state.dateOfBirth}
                                        disabled={this.state.oldValues.dateOfBirth != "" && this.state.oldValues.dateOfBirth != undefined }
                                        required
                                    />
                                </div>
                                <div className="col">
                                    <label htmlFor="pinCode">{formFieldDict[lang]["Pincode"] || "Pincode"}*</label>
                                    <input
                                        className="form-control"
                                        id="pinCode"
                                        name="pinCode"
                                        value={this.state.pinCode}
                                        onChange={(e) => this.handleZipCodeChange(e)}
                                        placeholder={formFieldDict[lang]["Enter here"] || "Enter here"}
                                    />
                                </div>
                            </div>



                            <div className="row">
                                <div className="col">
                                    <label htmlFor="addressLine1">{formFieldDict[lang]["Address Line 1"] || "Address Line 1"}*</label>
                                    <input
                                        className="form-control"
                                        id="addressLine1"
                                        name="addressLine1"
                                        value={this.state.addressLine1}
                                        onChange={(e) => this.setState({ addressLine1: e.target.value })}
                                        placeholder={formFieldDict[lang]["Enter here"] || "Enter here"}
                                        required
                                    />
                                </div>
                                <div className="col">
                                    <label htmlFor="addressLine2">{formFieldDict[lang]["Address Line 2"] || "Address Line 2"}</label>
                                    <input
                                        className="form-control"
                                        id="addressLine2"
                                        name="addressLine2"
                                        value={this.state.addressLine2}
                                        onChange={(e) => this.setState({ addressLine2: e.target.value })}
                                        placeholder={formFieldDict[lang]["Enter here"] || "Enter here"}
                                    />
                                </div>
                            </div>


                            <div className="row">
                                <div className="col">
                                    <label htmlFor='country'>{formFieldDict[lang]["Country"] || "Country"}*</label>
                                    <Select
                                        name='countryTitle'
                                        id='countryTitle'
                                        onChange={this.handleCountryChange}
                                        value={selectedCountryOption}
                                        required
                                        options={this.state.countries}
                                        isDisabled={true}
                                    />
                                </div>
                                <div className="col">
                                    <label htmlFor='state'>{formFieldDict[lang]["State"] || "State"}*</label>
                                    <Select
                                        name='state'
                                        id='state'
                                        onChange={this.handleStateChange}
                                        value={selectedStateOption}
                                        required
                                        options={this.state.states}
                                    />
                                </div>
                                <div className='col'>
                                    <label htmlFor='city'>{formFieldDict[lang]["City"] || "City"}*</label>
                                    <Select
                                        name='city'
                                        id='city'
                                        onChange={this.handleCityChange}
                                        value={selectedCityOption}
                                        required
                                        options={this.state.cities}
                                    />
                                </div>
                            </div>


                            <div className='form-group'>
                                <label htmlFor='referralSource'>{formFieldDict[lang]["Referral Source"] || "Referral Source"}*</label>
                                <select
                                    style={{
                                        width: '100%',
                                        display: 'block',
                                        height: '40px',
                                        lineHeight: '1.5',
                                        color: '#495057',
                                        backgroundColor: '#fff',
                                        backgroundClip: 'padding-box',
                                        border: '1px solid #ced4da',
                                        borderRadius: '5px',
                                        transition: 'border-color 0.15s ease-in-out, box-shadow 0.15s ease-in-out',
                                    }}
                                    name='referralSource'
                                    id='referralSource'
                                    value={this.state.referralSource}
                                    required
                                    onChange={(e) => this.setState({ referralSource: e.target.value })}
                                >
                                    <option value="" className="">{formFieldDict[lang]["Select Referral"] || "Select Referral"}</option>
                                    <option value="Ad flier with newspaper">{formFieldDict[lang]["Ad flier with newspaper"] || "Ad flier with newspaper"}</option>
                                    <option value="Ad flier at market/outdoors">{formFieldDict[lang]["Ad flier at market/outdoors"] || "Ad flier at market/outdoors"}</option>
                                    <option value="Saw an ad on a website">{formFieldDict[lang]["Saw an ad on a website"] || "Saw an ad on a website"}</option>
                                    <option value="Read about it on a blog/forum">{formFieldDict[lang]["Read about it on a blog/forum"] || "Read about it on a blog/forum"}</option>
                                    <option value="Ad at Café Coffee Day">{formFieldDict[lang]["Ad at Café Coffee Day"] || "Ad at Café Coffee Day"}</option>
                                    <option value="Got an email from a friend/colleague">{formFieldDict[lang]["Got an email from a friend/colleague"] || "Got an email from a friend/colleague"}</option>
                                    <option value="Email from a web portal/service/jobsite">{formFieldDict[lang]["Email from a web portal/service/jobsite"] || "Email from a web portal/service/jobsite"}</option>
                                    <option value="Searched on google.com">{formFieldDict[lang]["Searched on google.com"] || "Searched on google.com"}</option>
                                    <option value="Other">{formFieldDict[lang]["Other"] || "Other"}</option>
                                    <option value="Searched on another search engine">{formFieldDict[lang]["Searched on another search engine"] || "Searched on another search engine"}</option>
                                    <option value="Ad at a shop/restaurant">{formFieldDict[lang]["Ad at a shop/restaurant"] || "Ad at a shop/restaurant"}</option>
                                    <option value="Word of mouth from friend/colleague">{formFieldDict[lang]["Word of mouth from friend/colleague"] || "Word of mouth from friend/colleague"}</option>
                                </select>
                            </div>
                            </>
                            )}

                            <hr />
                            <div className="mt-2 d-flex justify-content-center">
                                <button
                                    type="submit"
                                    disabled={false}
                                    className="btn btn-primary mr-3"
                                >
                                    {formFieldDict[lang]["Submit"] || "Submit"}
                                </button>

                                {!IS_JAPAN_PORTAL && (
                                <button
                                    type="button"
                                    disabled={false}
                                    onClick={() => this.reset()}
                                    className="btn btn-light mr-3"
                                >
                                    {formFieldDict[lang]["Reset"] || "Reset"}
                                </button>
                                )}

                                <Show when={this.state.status === PageStatus.Submitting}>
                                    <Spinner animation="border" variant="primary" />
                                </Show>
                            </div>

                        </form>
                    </Container>
                </Modal.Body>
            </Modal>
        );
    }
}


const BasicProfileFormRedux = reduxForm({
    form: 'labelsBasicProfileForm',
})(withTranslation()(BasicProfile));

const BasicProfileFormWithRouter = withRouter(BasicProfileFormRedux);

export { BasicProfileFormWithRouter as BasicProfile };
